import { useEffect, useCallback } from 'react'
import { useAuth } from 'react-oidc-context'
import { User } from 'oidc-client-ts'
import { useAuthStore, extractUser } from '@/stores/auth-store'
import { TOKEN_STORAGE_KEY } from '@/lib/auth/token-storage'

export function TokenSync() {
  const auth = useAuth()
  const { setUser, setLoading, setError, logout } = useAuthStore()

  // Helper to sync token to localStorage
  const syncToken = useCallback((user: User | null | undefined) => {
    if (user?.access_token) {
      localStorage.setItem(TOKEN_STORAGE_KEY, user.access_token)
      const extractedUser = extractUser(user.profile)
      setUser(extractedUser)
    }
  }, [setUser])

  useEffect(() => {
    // Handle loading state
    if (auth.isLoading) {
      setLoading(true)
      return
    }

    // Handle authentication state
    if (auth.isAuthenticated && auth.user) {
      syncToken(auth.user)
    } else {
      // Clear token and user
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      logout()
    }
  }, [auth.isLoading, auth.isAuthenticated, auth.user, syncToken, setLoading, logout])

  // Handle token renewal events
  useEffect(() => {
    // Called when a new user/token is loaded (including after silent renewal)
    const handleUserLoaded = (user: User) => {
      console.debug('[TokenSync] User loaded, syncing token')
      syncToken(user)
    }

    // Called when token is about to expire (gives time to prepare)
    const handleAccessTokenExpiring = () => {
      console.debug('[TokenSync] Access token expiring, renewal should happen automatically')
    }

    // Called when token has expired
    const handleAccessTokenExpired = () => {
      console.warn('[TokenSync] Access token expired')
    }

    // Called when silent renewal fails
    const handleSilentRenewError = (error: Error) => {
      console.error('[TokenSync] Silent renewal failed:', error)
      setError('Session renewal failed. Please log in again.')
      // Clear token and redirect to login
      localStorage.removeItem(TOKEN_STORAGE_KEY)
      logout()
    }

    // Subscribe to events
    const unsubscribeUserLoaded = auth.events.addUserLoaded(handleUserLoaded)
    const unsubscribeExpiring = auth.events.addAccessTokenExpiring(handleAccessTokenExpiring)
    const unsubscribeExpired = auth.events.addAccessTokenExpired(handleAccessTokenExpired)
    const unsubscribeSilentRenewError = auth.events.addSilentRenewError(handleSilentRenewError)

    return () => {
      unsubscribeUserLoaded()
      unsubscribeExpiring()
      unsubscribeExpired()
      unsubscribeSilentRenewError()
    }
  }, [auth.events, syncToken, setError, logout])

  // Handle errors
  useEffect(() => {
    if (auth.error) {
      console.error('Auth error:', auth.error)
      setError(auth.error.message)
    }
  }, [auth.error, setError])

  return null
}
